import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  // ProductSelectEvent promises can settle out of order during rapid variant selection.
  // Only the newest selection owned by this gallery may replace its renderer.
  #selectedVariantVersion = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectedVariantVersion = ++this.#selectedVariantVersion;

    event.promise
      .then(async ({ detail }) => {
        if (selectedVariantVersion !== this.#selectedVariantVersion || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // R22 (2026-09-24): keep the current image on screen until the picked colour's image is
        // decoded (capped), so the swap never shows a blank frame. A newer pick made meanwhile wins.
        await preloadSelectedMedia(newMediaGallery);
        if (selectedVariantVersion !== this.#selectedVariantVersion) return;

        // A re-render after a colour pick is not a first appearance: the quick-add sheet's
        // entrance fade (quick-add-modal-styles) must not replay on it. The slide it opens on is
        // visible from the first frame (the server marks only slide 0; the quick-add sheet hides
        // aria-hidden slides until the slideshow's observer catches up).
        newMediaGallery.setAttribute('data-stz-replaced', '');
        openingSlide(newMediaGallery)?.setAttribute('aria-hidden', 'false');
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * The slide a (re-rendered) gallery opens on: its slideshow's initial-slide.
 * @param {Element} gallery
 * @returns {Element | undefined}
 */
function openingSlide(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  const initialSlide = Number(slideshow?.getAttribute('initial-slide')) || 0;
  return gallery.querySelectorAll('slideshow-slide')[initialSlide];
}

/** Longest a colour swap waits for its image before swapping anyway (R22). */
export const SELECTED_MEDIA_PRELOAD_CAP_MS = 800;

/**
 * Decodes the images the re-rendered gallery shows the moment it is inserted — the slide it opens
 * on plus any slide the server already marks visible (the quick-add sheet shows two) — before it
 * is shown. Each request is the same URL the new gallery would request on insertion, only earlier
 * (the unchanged ones are cache hits; in iOS Safari a fresh <img> still paints blank for a few
 * frames unless decoded first). Resolves when all decode, fail, or at the cap; never rejects.
 *
 * @param {Element} gallery - The re-rendered <media-gallery>, not yet in the document.
 * @returns {Promise<void>}
 */
export function preloadSelectedMedia(gallery) {
  const opening = openingSlide(gallery);
  const slides = [...gallery.querySelectorAll('slideshow-slide')].filter(
    (slide) => slide === opening || slide.getAttribute('aria-hidden') === 'false'
  );
  const sources = slides.map((slide) => slide.querySelector('img')).filter((img) => img?.getAttribute('src'));
  if (!sources.length) return Promise.resolve();

  const decodes = sources.map((source) => {
    const image = new Image();
    const sizes = source.getAttribute('sizes');
    const srcset = source.getAttribute('srcset');
    if (sizes) image.sizes = sizes;
    if (srcset) image.srcset = srcset;
    image.src = source.getAttribute('src');
    return image.decode().catch(() => {});
  });

  return new Promise((resolve) => {
    const timer = setTimeout(resolve, SELECTED_MEDIA_PRELOAD_CAP_MS);
    Promise.all(decodes).finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
